import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Description as DescriptionIcon, Search as SearchIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { insiderApi } from '../../services/api';

function Signals() {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [signalType, setSignalType] = useState('');
  const [tickerSearch, setTickerSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [modelPerf, setModelPerf] = useState(null);
  const [perfLoading, setPerfLoading] = useState(true);

  useEffect(() => {
    loadSignals();
  }, [page, rowsPerPage, signalType, tickerSearch]);

  useEffect(() => {
    loadModelPerformance();
  }, []);

  const loadSignals = async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, per_page: rowsPerPage };
      if (signalType) params.signal_type = signalType;
      if (tickerSearch.trim()) params.ticker = tickerSearch.trim().toUpperCase();

      const response = await insiderApi.getSignals(params);
      setSignals(response.data.signals || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error loading signals:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadModelPerformance = async () => {
    try {
      setPerfLoading(true);
      const response = await insiderApi.getModelPerformance();
      setModelPerf(response.data);
    } catch (err) {
      console.error('Error loading model performance:', err);
    } finally {
      setPerfLoading(false);
    }
  };

  const getSignalColor = (type) => {
    switch (type) {
      case 'STRONG_BUY': return { bg: '#1b5e20', color: 'white' };
      case 'BUY': return { bg: '#4caf50', color: 'white' };
      case 'SKIP': return { bg: '#9e9e9e', color: 'white' };
      default: return { bg: '#9e9e9e', color: 'white' };
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatMarketCap = (value) => {
    if (!value) return '-';
    if (value >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
    return `$${value.toLocaleString()}`;
  };

  // Generate SEC EDGAR Form 4 URL from CIK and accession number (x_ref)
  const getForm4Url = (cik, xRef) => {
    if (!cik || !xRef) return null;
    // x_ref format: "0001213900-25-120948"
    // SEC URL needs accession without dashes: "0001213900-25-120948" -> "000121390025120948"
    const accessionNoDashes = xRef.replace(/-/g, '');
    return `https://www.sec.gov/Archives/edgar/data/${cik}/${accessionNoDashes}/${xRef}-index.htm`;
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        ML Trading Signals
      </Typography>

      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search by ticker..."
          value={tickerSearch}
          onChange={(e) => { setTickerSearch(e.target.value); setPage(0); }}
          sx={{ minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Signal Type</InputLabel>
          <Select
            value={signalType}
            label="Signal Type"
            onChange={(e) => { setSignalType(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="STRONG_BUY">Strong Buy</MenuItem>
            <MenuItem value="BUY">Buy</MenuItem>
            <MenuItem value="SKIP">Skip</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Live Success Rates */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight="bold">ML Model Success Rates by Confidence Level</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {perfLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : modelPerf ? (
            <>
              {[
                { label: 'V1 (Production)', data: modelPerf.v1, color: '#e8f5e9', totalSignals: modelPerf.total_v1_signals },
                { label: 'V3 Shadow (Catalyst-Enriched)', data: modelPerf.v3, color: '#e8f4fd', totalSignals: modelPerf.total_v3_signals },
              ].map((model) => (
                <Box key={model.label} sx={{ mb: 3 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Confidence</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">Success Rate</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">Signals</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={4}>
                            <strong>{model.label}</strong>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              ({model.totalSignals} total resolved signals)
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {model.data.filter(b => b.total > 0).map((bucket) => {
                          const pct = bucket.threshold * 100;
                          const isActiveBuy = modelPerf.thresholds.min_confidence_buy &&
                            Math.abs(bucket.threshold - modelPerf.thresholds.min_confidence_buy) < 0.01;
                          const isActiveStrongBuy = modelPerf.thresholds.min_confidence_strong_buy &&
                            Math.abs(bucket.threshold - modelPerf.thresholds.min_confidence_strong_buy) < 0.01;
                          const isActive = isActiveBuy || isActiveStrongBuy;
                          return (
                            <TableRow
                              key={pct}
                              sx={{
                                bgcolor: isActive ? (model.color === '#e8f5e9' ? '#c8e6c9' : '#bbdefb') : model.color,
                                borderLeft: isActive ? '3px solid #1976d2' : 'none',
                              }}
                            >
                              <TableCell>
                                <strong>≥ {pct}%</strong>
                                {isActiveBuy && (
                                  <Chip label="Active BUY threshold" size="small" sx={{ ml: 1, fontSize: '0.65rem', height: 20, bgcolor: '#1976d2', color: 'white' }} />
                                )}
                                {isActiveStrongBuy && (
                                  <Chip label="Active STRONG_BUY threshold" size="small" sx={{ ml: 1, fontSize: '0.65rem', height: 20, bgcolor: '#1b5e20', color: 'white' }} />
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <strong>{(bucket.success_rate * 100).toFixed(1)}%</strong>
                              </TableCell>
                              <TableCell align="right">
                                {bucket.total}
                                {bucket.total < 20 && (
                                  <Typography variant="caption" color="warning.main" sx={{ ml: 0.5 }}>
                                    (low sample)
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {bucket.successes} hit target
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {model.data.every(b => b.total === 0) && (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography color="text.secondary" variant="body2">No resolved signals yet</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
              <Typography variant="body2" color="text.secondary">
                Success = 21%+ return within 60 days of insider filing. Live data since Feb 2025.
              </Typography>
            </>
          ) : (
            <Typography color="text.secondary">Failed to load performance data</Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Signal</TableCell>
                  <TableCell align="right">V1 Confidence</TableCell>
                  <TableCell align="right">V3 (Catalyst)</TableCell>
                  <TableCell align="right">Return</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">MCap</TableCell>
                  <TableCell align="center">Exit Window</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Probability of 10%+ drawdown before achieving 21% target gain">
                      <span style={{ cursor: 'help' }}>Risk</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">Form 4</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {signals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography color="text.secondary" py={3}>
                        No signals found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  signals.map((signal, idx) => {
                    const colors = getSignalColor(signal.signal_type);
                    const form4Url = getForm4Url(signal.cik, signal.x_ref);
                    return (
                      <TableRow key={idx}>
                        <TableCell>{formatDate(signal.generated_at)}</TableCell>
                        <TableCell>
                          <Typography fontWeight="bold">{signal.symbol}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={signal.signal_type}
                            size="small"
                            sx={{ bgcolor: colors.bg, color: colors.color, fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {(signal.confidence * 100).toFixed(1)}%
                        </TableCell>
                        <TableCell align="right">
                          {signal.v3_confidence ? (
                            <Chip
                              label={`${(signal.v3_confidence * 100).toFixed(1)}% ${signal.v3_recommendation || ''}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderStyle: 'dashed',
                                borderColor: signal.v3_recommendation === 'STRONG_BUY' ? '#1b5e20' :
                                             signal.v3_recommendation === 'BUY' ? '#4caf50' : '#9e9e9e',
                                color: signal.v3_recommendation === 'STRONG_BUY' ? '#1b5e20' :
                                       signal.v3_recommendation === 'BUY' ? '#4caf50' : '#666',
                                fontSize: '0.75rem',
                              }}
                            />
                          ) : (
                            <Typography color="text.disabled" variant="body2">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {signal.current_return != null ? (
                            <Chip
                              label={`${signal.current_return >= 0 ? '+' : ''}${(signal.current_return * 100).toFixed(1)}%`}
                              size="small"
                              sx={{
                                fontWeight: 'bold',
                                bgcolor: signal.hit_target ? '#1b5e20' :
                                         signal.current_return >= 0.10 ? '#4caf50' :
                                         signal.current_return >= 0 ? '#e8f5e9' :
                                         signal.current_return >= -0.10 ? '#fff3e0' :
                                         '#ffebee',
                                color: signal.hit_target ? 'white' :
                                       signal.current_return >= 0.10 ? 'white' :
                                       signal.current_return >= 0 ? '#2e7d32' :
                                       signal.current_return >= -0.10 ? '#e65100' :
                                       '#c62828',
                                fontSize: '0.75rem',
                              }}
                            />
                          ) : (
                            <Typography color="text.disabled" variant="body2">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          ${signal.price_at_signal?.toFixed(2) || '-'}
                        </TableCell>
                        <TableCell align="right">
                          {formatMarketCap(signal.market_cap)}
                        </TableCell>
                        <TableCell align="center">
                          {signal.exit_window_start && signal.exit_window_end ? (
                            <Tooltip title={`Exit timing confidence: ${signal.exit_timing_confidence ? (signal.exit_timing_confidence * 100).toFixed(0) + '%' : 'N/A'}`}>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                {new Date(signal.exit_window_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                {' - '}
                                {new Date(signal.exit_window_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {signal.risk_level ? (
                            <Tooltip title={`Drawdown probability: ${signal.drawdown_probability ? (signal.drawdown_probability * 100).toFixed(0) + '%' : 'N/A'}`}>
                              <Chip
                                label={signal.risk_level}
                                size="small"
                                sx={{
                                  bgcolor: signal.risk_level === 'LOW' ? '#e8f5e9' :
                                           signal.risk_level === 'MEDIUM' ? '#fff3e0' : '#ffebee',
                                  color: signal.risk_level === 'LOW' ? '#2e7d32' :
                                         signal.risk_level === 'MEDIUM' ? '#e65100' : '#c62828',
                                  fontWeight: 'bold',
                                  fontSize: '0.7rem',
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <Typography color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {form4Url ? (
                            <Tooltip title="View SEC Form 4 Filing">
                              <IconButton
                                size="small"
                                href={form4Url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: 'primary.main' }}
                              >
                                <DescriptionIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </Paper>
      )}
    </Box>
  );
}

export default Signals;
// v3-shadow-deploy-1771975383
